import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useNavigate, useOutletContext } from "react-router-dom";
import io from "socket.io-client";
import { useAuth } from "../../services/AuthContext";
import { clearStoredAuthToken, getStoredAuthToken } from "../../services/authStorage";
import { getLanguageCopy, normalizeLanguage } from "../../services/language";
import { setLanguage as setI18nLanguage } from "../../i18n";
import { translations } from "../../translations";
import {
  buildCurrentUserProfileIdentitySet,
  resolvePlayerProfileRoute,
  resolveProfileTargetFromUserLike,
} from "./leaderboardProfileNavigation";
import { resolveAvatarSrc } from "../../utils/avatarUrl";

const RANKING_LIMIT_PARAM = "all";
const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost:5001");
const ONLINE_PLAYERS_COUNT_UPDATED_EVENT = "online_players_count_updated";
const ONLINE_PLAYERS_COUNT_REQUEST_EVENT = "online_players_count_request";
const LEGACY_CONNECTED_PLAYERS_COUNT_EVENT = "CONNECTED_PLAYERS_COUNT";
const LEGACY_CONNECTED_PLAYERS_COUNT_REQUEST_EVENT = "CONNECTED_PLAYERS_COUNT_REQUEST";
const CONNECTED_PLAYERS_COUNT_EVENTS = [
  ONLINE_PLAYERS_COUNT_UPDATED_EVENT,
  LEGACY_CONNECTED_PLAYERS_COUNT_EVENT,
];

const MODE_SECTIONS = [
  {
    key: "ciencia",
    titleKey: "RANKING_SCIENCE_MODE",
    shortTitleKey: "RANKING_SCIENCE",
    icon: "/mode-icons/free.png",
  },
  {
    key: "cienzar",
    titleKey: "RANKING_CIENZAR_MODE",
    shortTitleKey: "RANKING_CIENZAR",
    icon: "/mode-icons/cienzar.png",
  },
  {
    key: "sciencevsazar",
    titleKey: "RANKING_CONTRAZAR_MODE",
    shortTitleKey: "RANKING_CONTRAZAR",
    icon: "/mode-icons/logo-contrazar.png",
  },
  {
    key: "azar",
    titleKey: "RANKING_CHANCE_MODE",
    shortTitleKey: "RANKING_CHANCE",
    icon: "/mode-icons/dice.png",
  },
  {
    key: "global",
    titleKey: "RANKING_GLOBAL_POSITION",
    shortTitleKey: "RANKING_GLOBAL",
    icon: "/mode-icons/players-1v1.png",
    scoreBadgeKey: "RANKING_TOTAL_CIMAX",
    scoreHeaderKey: "RANKING_TOTAL",
  },
];

const buildEmptyRankingMap = () =>
  MODE_SECTIONS.reduce((accumulator, section) => {
    accumulator[section.key] = [];
    return accumulator;
  }, {});

const GLOBAL_RANKING_MODE_KEYS = ["ciencia", "cienzar", "sciencevsazar", "azar"];

const normalizeCimaxScore = (value) => {
  const source =
    value && typeof value === "object"
      ? value.score ?? value.cimax ?? value.value ?? value.points ?? value.total
      : value;
  const numericValue = Number(source);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.round(numericValue));
};

const hasOnlinePresence = (entry) =>
  Boolean(entry?.online || entry?.isOnline || entry?.connected || entry?.isConnected);

const getRankingDisplayName = (entry) => {
  const raw = String(
    entry?.displayName ||
      entry?.firstName ||
      entry?.username ||
      entry?.userName ||
      entry?.email ||
      entry?.userId ||
      "Jugador",
  ).trim();

  if (!raw) return "Jugador";
  if (raw.includes("@")) return String(raw.split("@")[0] || "Jugador").trim() || "Jugador";
  return raw;
};

const getRankingInitials = (value) => {
  const words = String(value || "Jugador")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0]?.[0] || "J";
  const second = words.length > 1 ? words[1]?.[0] || "" : words[0]?.[1] || "";
  return `${first}${second}`.toUpperCase();
};

const getRankingIdentity = (entry) => {
  const candidates = [
    entry?.userId,
    entry?.userIdNormalized,
    entry?.loginIdentifier,
    entry?.email,
    entry?.id,
    entry?._id,
    entry?.identity,
    entry?.username,
    entry?.userName,
    entry?.playerCode,
    getRankingDisplayName(entry),
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const normalized = String(candidates[index] || "").trim().toLowerCase();
    if (normalized) return normalized;
  }

  return "";
};

const buildGlobalRankingFromModes = (rankingsByMode) => {
  const playersByIdentity = new Map();

  GLOBAL_RANKING_MODE_KEYS.forEach((modeKey) => {
    const modePlayers = Array.isArray(rankingsByMode?.[modeKey])
      ? rankingsByMode[modeKey]
      : [];

    modePlayers.forEach((player) => {
      const identity = getRankingIdentity(player);
      if (!identity) return;

      const displayName = getRankingDisplayName(player);
      const cimaxScore = normalizeCimaxScore(player?.cimaxScore);
      const existing = playersByIdentity.get(identity);

      if (existing) {
        existing.cimaxScore += cimaxScore;
        existing.cimaxScores[modeKey] = cimaxScore;
        existing.playerCode = existing.playerCode || player?.playerCode || "";
        existing.avatarUrl = existing.avatarUrl || resolveAvatarSrc(player?.avatarUrl || "");
        existing.online = Boolean(existing.online || hasOnlinePresence(player));
        return;
      }

      playersByIdentity.set(identity, {
        ...player,
        rank: 0,
        userId: String(player?.userId || identity).trim().toLowerCase(),
        displayName,
        playerCode: String(player?.playerCode || "").trim().toUpperCase(),
        avatarUrl: resolveAvatarSrc(player?.avatarUrl || ""),
        online: hasOnlinePresence(player),
        wins: 0,
        cimaxScores: {
          ciencia: 0,
          cienzar: 0,
          sciencevsazar: 0,
          azar: 0,
          [modeKey]: cimaxScore,
        },
        cimaxScore,
      });
    });
  });

  return Array.from(playersByIdentity.values())
    .sort((left, right) => {
      const scoreDiff =
        normalizeCimaxScore(right?.cimaxScore) - normalizeCimaxScore(left?.cimaxScore);
      if (scoreDiff !== 0) return scoreDiff;

      const nameComparison = getRankingDisplayName(left).localeCompare(
        getRankingDisplayName(right),
        "es",
        { sensitivity: "base" },
      );
      if (nameComparison !== 0) return nameComparison;

      return getRankingIdentity(left).localeCompare(getRankingIdentity(right));
    })
    .map((player, index) => ({
      ...player,
      rank: index + 1,
      cimaxScore: normalizeCimaxScore(player?.cimaxScore),
    }));
};

const normalizeRankingModes = (modes) => {
  const next = buildEmptyRankingMap();

  MODE_SECTIONS.forEach((section) => {
    next[section.key] = (Array.isArray(modes?.[section.key]) ? modes[section.key] : [])
      .map((entry, index) => ({
        ...entry,
        rank: Math.max(1, Number(entry?.rank) || index + 1),
        userId: String(entry?.userId || "").trim().toLowerCase(),
        id: String(entry?.id || "").trim(),
        _id: String(entry?._id || "").trim(),
        identity: String(entry?.identity || "").trim(),
        userIdNormalized: String(entry?.userIdNormalized || "").trim(),
        loginIdentifier: String(entry?.loginIdentifier || "").trim(),
        email: String(entry?.email || "").trim(),
        username: String(entry?.username || "").trim(),
        userName: String(entry?.userName || "").trim(),
        displayName: getRankingDisplayName(entry),
        playerCode: String(entry?.playerCode || "").trim().toUpperCase(),
        avatarUrl: resolveAvatarSrc(entry?.avatarUrl || entry?.avatar || entry?.image || ""),
        online: hasOnlinePresence(entry),
        wins: Math.max(0, Number(entry?.wins) || 0),
        cimaxScore: normalizeCimaxScore(entry?.cimaxScore),
      }));
  });

  if (!next.global.length) {
    next.global = buildGlobalRankingFromModes(next);
  }

  return next;
};

const handleRankingSessionInvalidated = () => {
  delete axios.defaults.headers["Authorization"];
  clearStoredAuthToken();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
};

const verifyRankingSocketSession = (socket) => {
  if (!socket || typeof socket.emit !== "function") return;
  const token = String(getStoredAuthToken() || "0");
  socket.emit("verify", token);
  socket.emit(ONLINE_PLAYERS_COUNT_REQUEST_EVENT);
  socket.emit(LEGACY_CONNECTED_PLAYERS_COUNT_REQUEST_EVENT);
};

const ensureRankingSharedSocket = () => {
  if (typeof window === "undefined") return null;

  const existingSocket = window.__ximaSocket || window.socket || null;
  if (existingSocket) {
    if (typeof existingSocket.off === "function") {
      existingSocket.off("SESSION_INVALIDATED", handleRankingSessionInvalidated);
    }
    if (typeof existingSocket.on === "function") {
      existingSocket.on("SESSION_INVALIDATED", handleRankingSessionInvalidated);
    }
    if (
      existingSocket.disconnected &&
      typeof existingSocket.connect === "function"
    ) {
      existingSocket.connect();
    }
    if (existingSocket.connected) {
      verifyRankingSocketSession(existingSocket);
    }
    window.__ximaSocket = existingSocket;
    window.socket = existingSocket;
    return existingSocket;
  }

  const sharedSocket = io(SOCKET_URL, {
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 800,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
  });

  sharedSocket.on("connect", () => {
    verifyRankingSocketSession(sharedSocket);
  });
  sharedSocket.on("SESSION_INVALIDATED", handleRankingSessionInvalidated);

  window.__ximaSocket = sharedSocket;
  window.socket = sharedSocket;
  return sharedSocket;
};

const Ranking = () => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language);
  const currentUserProfileIdentitySet = useMemo(
    () => buildCurrentUserProfileIdentitySet(user),
    [user],
  );
  const text = useCallback(
    (key, fallback = key) => getLanguageCopy(language, translations[key], fallback),
    [language],
  );

  const [rankingsByMode, setRankingsByMode] = useState(buildEmptyRankingMap);
  const [loading, setLoading] = useState(true);
  const [generatedAt, setGeneratedAt] = useState("");
  const mountedRef = useRef(false);
  const rankingRefreshTimerRef = useRef(null);

  useEffect(() => {
    setI18nLanguage(language);
  }, [language]);

  const loadRanking = useCallback(async ({ showLoading = false, showError = true } = {}) => {
    if (showLoading && mountedRef.current) setLoading(true);
    try {
      const response = await axios.get("/tournaments/leaderboard/modes", {
        params: { limit: RANKING_LIMIT_PARAM },
      });
      if (!mountedRef.current) return;
      setRankingsByMode(normalizeRankingModes(response?.data?.modes || {}));
      setGeneratedAt(String(response?.data?.generatedAt || ""));
    } catch (error) {
      if (showError && mountedRef.current) {
        notification.error({
          message: text("RANKING_ERROR"),
          description:
            error?.response?.data?.message ||
            text("RANKING_LOAD_ERROR"),
        });
      }
    } finally {
      if (showLoading && mountedRef.current) setLoading(false);
    }
  }, [text]);

  useEffect(() => {
    mountedRef.current = true;
    loadRanking({ showLoading: true });

    return () => {
      mountedRef.current = false;
      if (rankingRefreshTimerRef.current) {
        window.clearTimeout(rankingRefreshTimerRef.current);
        rankingRefreshTimerRef.current = null;
      }
    };
  }, [loadRanking]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const rankingSocket = ensureRankingSharedSocket();
    if (!rankingSocket || typeof rankingSocket.on !== "function") return undefined;

    const requestPresenceSnapshot = () => {
      verifyRankingSocketSession(rankingSocket);
    };

    const scheduleRankingRefresh = () => {
      if (rankingRefreshTimerRef.current) {
        window.clearTimeout(rankingRefreshTimerRef.current);
      }
      rankingRefreshTimerRef.current = window.setTimeout(() => {
        rankingRefreshTimerRef.current = null;
        loadRanking({ showError: false });
      }, 350);
    };

    if (typeof rankingSocket.off === "function") {
      rankingSocket.off("connect", requestPresenceSnapshot);
      CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
        rankingSocket.off(eventName, scheduleRankingRefresh);
      });
    }

    rankingSocket.on("connect", requestPresenceSnapshot);
    CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
      rankingSocket.on(eventName, scheduleRankingRefresh);
    });
    requestPresenceSnapshot();

    return () => {
      if (typeof rankingSocket.off === "function") {
        rankingSocket.off("connect", requestPresenceSnapshot);
        CONNECTED_PLAYERS_COUNT_EVENTS.forEach((eventName) => {
          rankingSocket.off(eventName, scheduleRankingRefresh);
        });
      }
    };
  }, [loadRanking]);

  const openPlayerProfile = useCallback(
    (player) => {
      if (!resolveProfileTargetFromUserLike(player)) return;
      const route = resolvePlayerProfileRoute(player, currentUserProfileIdentitySet);
      if (route) navigate(route);
    },
    [currentUserProfileIdentitySet, navigate],
  );

  const lastUpdatedLabel = generatedAt
    ? new Date(generatedAt).toLocaleString()
    : "";

  return (
    <main className="xima-gold-lobby xima-full-ranking-page">
      <header className="xima-full-ranking-header">
        <button type="button" onClick={() => navigate("/game/lobby")}>
          {text("RANKING_BACK_TO_LOBBY")}
        </button>
        <div>
          <span>{text("RANKING_TOP_PLAYERS")}</span>
          <h1>{text("RANKING_FULL_RANKING")}</h1>
        </div>
        <strong>{text("RANKING_ALL_PLAYERS_BY_MODE_GLOBAL")}</strong>
      </header>

      <section className="xima-full-ranking-grid" aria-label={text("RANKING_ARIA_BY_MODE_GLOBAL")}>
        {MODE_SECTIONS.map((section) => {
          const players = Array.isArray(rankingsByMode?.[section.key])
            ? rankingsByMode[section.key]
            : [];
          return (
            <article
              key={section.key}
              className={`xima-full-ranking-panel xima-full-ranking-panel-${section.key}`}
            >
              <div className="xima-full-ranking-panel-head">
                <div>
                  <img src={section.icon} alt="" aria-hidden="true" />
                  <span>
                    <small>{text(section.shortTitleKey)}</small>
                    <strong>{text(section.titleKey)}</strong>
                  </span>
                </div>
                <b>{text(section.scoreBadgeKey || "RANKING_CIMAX")}</b>
              </div>

              <div className="xima-full-ranking-column-head">
                <span>#</span>
                <span>{text("RANKING_PLAYER")}</span>
                <span>{text(section.scoreHeaderKey || "RANKING_SCORE")}</span>
              </div>

              <div className="xima-full-ranking-list">
                {loading ? (
                  <div className="xima-full-ranking-state">{text("RANKING_LOADING")}</div>
                ) : players.length > 0 ? (
                  players.map((player, index) => {
                    const rank = Math.max(1, Number(player?.rank) || index + 1);
                    const displayName = getRankingDisplayName(player);
                    const avatarUrl = resolveAvatarSrc(player?.avatarUrl || "");
                    const isPlayerOnline =
                      section.key === "global" && hasOnlinePresence(player);
                    const profileRoute = resolvePlayerProfileRoute(
                      player,
                      currentUserProfileIdentitySet,
                    );
                    return (
                      <button
                        key={`${section.key}-${player?.userId || displayName}-${rank}`}
                        type="button"
                        className="xima-full-ranking-row"
                        onClick={profileRoute ? () => openPlayerProfile(player) : undefined}
                        disabled={!profileRoute}
                      >
                        <span className={`xima-rank-number xima-rank-${rank}`}>{rank}</span>
                        <span className="xima-full-ranking-player">
                          <span className="xima-rank-avatar">
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={displayName} />
                            ) : (
                              getRankingInitials(displayName)
                            )}
                          </span>
                          <span>
                            <span className="xima-full-ranking-name-line">
                              {isPlayerOnline ? (
                                <span
                                  className="xima-full-ranking-online-dot"
                                  title={text("RANKING_CONNECTED")}
                                  aria-label={text("RANKING_CONNECTED")}
                                />
                              ) : null}
                              <strong>{displayName}</strong>
                            </span>
                            {player?.playerCode ? <small>{player.playerCode}</small> : null}
                          </span>
                        </span>
                        <span className="xima-full-ranking-score">
                          {normalizeCimaxScore(player?.cimaxScore)}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <div className="xima-full-ranking-state">{text("RANKING_NO_REAL_DATA")}</div>
                )}
              </div>
            </article>
          );
        })}
      </section>

      {lastUpdatedLabel ? (
        <footer className="xima-full-ranking-footer">
          {text("RANKING_UPDATED")}: {lastUpdatedLabel}
        </footer>
      ) : null}
    </main>
  );
};

export default Ranking;
